import { forwardRef } from 'react';
import { twMerge } from 'tailwind-merge';

import Fieldset from '../fieldset';
import { FloatingLabel, Label, LabelProps } from '../label';
import { colorClasses, sizeClasses, variantClasses } from './classes';
import {
  SelectContext,
  useMultiSelectContext,
  useSelectContext,
} from './context';
import { MultiSelect, MultiSelectOption } from './MultiSelect';
import {
  SelectOptionProps,
  SelectProps,
  SelectSingleProps,
  SelectWrapperProps,
} from './types';

const SelectValue = () => (
  <button type="button" className="flex flex-1 items-center min-w-0">
    <selectedcontent className="block min-w-0 truncate" />
  </button>
);

const SingleSelect = forwardRef<HTMLSelectElement, SelectSingleProps>(
  (
    {
      className,
      size = 'md',
      color = 'default',
      variant = 'default',
      error = false,
      children,
      ...props
    },
    ref,
  ) => {
    const { isInsideWrapper } = useSelectContext();

    const classes = twMerge(
      isInsideWrapper ? 'grow' : 'select',
      !isInsideWrapper && sizeClasses[size],
      !isInsideWrapper && colorClasses[color],
      !isInsideWrapper && variantClasses[variant],
      !isInsideWrapper && error && 'select-error',
      className,
    );

    return (
      <select ref={ref} className={classes} {...props}>
        <SelectValue />
        {children}
      </select>
    );
  },
);

const SelectBase = forwardRef<HTMLSelectElement, SelectProps>((props, ref) => {
  if (props.multiple) return <MultiSelect {...props} />;
  return <SingleSelect ref={ref} {...props} />;
});

const Wrapper = ({
  className,
  children,
  size = 'md',
  color = 'default',
  variant = 'default',
  error = false,
  disabled = false,
  floatingLabel,
}: SelectWrapperProps) => {
  const classes = twMerge(
    'select',
    sizeClasses[size],
    colorClasses[color],
    variantClasses[variant],
    error && 'select-error',
    disabled && 'select-disabled',
    className,
  );

  const content = <label className={classes}>{children}</label>;

  if (floatingLabel) {
    return (
      <SelectContext.Provider value={{ isInsideWrapper: true }}>
        <FloatingLabel label={floatingLabel}>{content}</FloatingLabel>
      </SelectContext.Provider>
    );
  }

  return (
    <SelectContext.Provider value={{ isInsideWrapper: true }}>
      {content}
    </SelectContext.Provider>
  );
};

const Option = forwardRef<HTMLOptionElement, SelectOptionProps>(
  (props, ref) => {
    const multiSelect = useMultiSelectContext();

    if (multiSelect)
      return <MultiSelectOption {...props} multiSelect={multiSelect} />;

    const { className, children, ...optionProps } = props;

    return (
      <option ref={ref} className={className} {...optionProps}>
        {children}
      </option>
    );
  },
);

const SelectLabel = (props: LabelProps<'span'>) => (
  <Label {...props} as="span" />
);

SelectBase.displayName = 'Select';
SingleSelect.displayName = 'Select.Single';
Option.displayName = 'Select.Option';
SelectLabel.displayName = 'Select.Label';

export const Select = Object.assign(SelectBase, {
  Wrapper,
  Option,
  Label: SelectLabel,
  Fieldset,
});
